/**
 * Anonymous interview draft, persisted to localStorage.
 *
 * The interview runs entirely signed-out (Journey spec §05: "no account
 * required until submit"), so the draft has nowhere else to live until the user
 * signs in and the scan claims it.
 *
 * THIS IS DEVICE-BOUND. A draft started on a phone is not on the laptop. The
 * spec calls for flagging that to users on long sessions rather than pretending
 * otherwise.
 */
import {
  DEFAULT_POST_AUTH,
  INTERVIEW_QUESTIONS,
  POST_AUTH_TARGETS,
  isKnownNext,
  resolveNext,
  type InterviewQuestionId,
} from '@ideabrief/core';

const KEY = 'ideabrief.draft.v1';

export interface DraftAnswer {
  answer: string;
  skipped: boolean;
}

export interface Draft {
  /** Index into INTERVIEW_QUESTIONS. */
  step: number;
  answers: Partial<Record<InterviewQuestionId, DraftAnswer>>;
  /** Inline edits made on the launch screen, keyed by brief section. */
  overrides: Record<string, string>;
  updatedAt: number;
}

const EMPTY: Draft = { step: 0, answers: {}, overrides: {}, updatedAt: 0 };

export function loadDraft(): Draft {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return { ...EMPTY };
    const parsed = JSON.parse(raw) as Partial<Draft>;
    // Defensive: a hand-edited or half-written value must not crash the app on
    // load — the interview is the one screen a user cannot afford to lose.
    return {
      step: typeof parsed.step === 'number' ? parsed.step : 0,
      answers: (parsed.answers ?? {}) as Draft['answers'],
      overrides: parsed.overrides ?? {},
      updatedAt: parsed.updatedAt ?? 0,
    };
  } catch {
    return { ...EMPTY };
  }
}

export function saveDraft(draft: Omit<Draft, 'updatedAt'>): void {
  try {
    localStorage.setItem(
      KEY,
      JSON.stringify({ ...draft, updatedAt: Date.now() }),
    );
  } catch {
    // Private browsing or a full quota. The interview continues in memory —
    // losing persistence is bad, losing the session is worse.
  }
}

/** Called once the scan has claimed the draft server-side. */
export function clearDraft(): void {
  try {
    localStorage.removeItem(KEY);
  } catch {
    // Nothing useful to do; the next save overwrites it anyway.
  }
}

export function hasDraft(): boolean {
  const d = loadDraft();
  return Object.keys(d.answers).length > 0;
}

/**
 * True once every question has an answer — typed or explicitly skipped.
 *
 * Decided from the ANSWERS, not from `step`, and that is the whole point.
 * `step` now ends at INTERVIEW_QUESTIONS.length (one past the end, the sentinel
 * a for-loop leaves behind), but drafts written before that fix stored the LAST
 * QUESTION'S INDEX instead — indistinguishable from "sitting on question 8
 * having answered nothing". Those drafts are sitting in real browsers right now,
 * so a check on `step` alone would leave every existing user with the bug that
 * prompted this: returning to /capture re-asked a question they had answered,
 * and they retyped work that was never lost. Verified against a legacy draft in
 * a real browser, which a step-only check did NOT rescue.
 */
export function isComplete(draft: Draft): boolean {
  return INTERVIEW_QUESTIONS.every((q) => draft.answers[q.id] !== undefined);
}

/**
 * Where a just-authenticated user should land.
 *
 * A valid `?next=` allowlist key always wins (resolved through core's
 * allowlist — never navigated to raw; open-redirect defence lives there).
 * With no valid key, the choice is DRAFT-AWARE: a user who signs in cold
 * while an interview draft sits in localStorage almost certainly came back to
 * finish it, so they go to /launch instead of an empty dashboard where the
 * draft would be stranded behind a banner. Both candidates are already
 * allowlisted paths, so this adds no new navigation surface.
 */
export function postAuthDestination(next: string | null | undefined): string {
  if (isKnownNext(next)) return resolveNext(next);
  return hasDraft() ? POST_AUTH_TARGETS.launch : DEFAULT_POST_AUTH;
}
